<template>
  <Modal
    :show="show.grantAccess"
    action-text="Grant Access"
    width="medium"
    :disabled="!selectedUser"
    @action="handleSubmit"
    @close="handleClose"
  >
    <template #title>
      Grant Access
    </template>

    <template #content>
      <p class="text-body-2 text-textLight mb-4">
        Search for a user by name or email to grant them editing access to this job profile.
      </p>

      <DpplTextField
        v-model="searchQuery"
        label="Search by name or email"
        variant="outlined"
        density="compact"
        clearable
        hide-details
        @click:clear="clearSearch"
      />

      <div
        class="mt-3"
      >
        <v-list
          density="compact"
          class="search-results pa-0"
          :class="{ 'opacity-50 pointer-events-none': isSearching }"
        >
          <v-list-item
            v-for="user in filteredResults"
            :key="user.ulid"
            :active="selectedUser?.ulid === user.ulid"
            color="primary"
            rounded="lg"
            class="px-3 py-2"
            @click="!user.disabled && selectUser(user)"
          >
            <v-list-item-title
              class="text-body-2"
              :class="{ 'opacity-50 pointer-events-none': user.disabled }"
            >
              {{ user.name }}
            </v-list-item-title>
            <v-list-item-subtitle
              class="text-caption"
              :class="{ 'opacity-50 pointer-events-none': user.disabled }"
            >
              {{ user.email }}
            </v-list-item-subtitle>
            <template
              v-if="user.disabled"
              #append
            >
              <DpplTooltip
                tooltip-placement="top"
                :tooltip-offset="8"
                teleport
              >
                <template #activator="{ onMouseenter, onMouseleave }">
                  <v-icon
                    icon="mdi-information-outline"
                    size="small"
                    class="text-textLight"
                    @mouseenter="onMouseenter"
                    @mouseleave="onMouseleave"
                  />
                </template>
                <template #tooltip-content>
                  Already has access to all profiles
                </template>
              </DpplTooltip>
            </template>
          </v-list-item>
        </v-list>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { debounce } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useModals } from '@/stores/modals/modals';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { searchDraftProfileUsers } from '@/api/jobProfile';
import Modal from '@/components/shared/Modal.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';

const { show } = storeToRefs(useModals());
const { setModal } = useModals();

const { jobProfile } = storeToRefs(useCurrentJobProfile());
const { grantAccess } = useCurrentJobProfile();

const searchQuery = ref('');
const searchResults = ref([]);
const selectedUser = ref(null);
const isSearching = ref(false);

const filteredResults = computed(() => {
  const existingUlids = new Set((jobProfile.value?.access_list ?? []).map((e) => e.ulid));
  return searchResults.value.filter((u) => !existingUlids.has(u.ulid));
});

const selectUser = (user) => {
  selectedUser.value = selectedUser.value?.ulid === user.ulid ? null : user;
};

const clearSearch = () => {
  searchQuery.value = '';
  searchResults.value = [];
  selectedUser.value = null;
};

const handleClose = () => {
  setModal({ field: 'grantAccess' });
  clearSearch();
};

const handleSubmit = async () => {
  if (!selectedUser.value) return;
  await grantAccess(selectedUser.value.ulid);
  clearSearch();
};

const runSearch = debounce(async (q) => {
  isSearching.value = true;
  try {
    const { users } = await searchDraftProfileUsers({
      q,
      draftUlid: jobProfile.value?.ulid,
    });
    searchResults.value = users;
  } catch {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
}, 500);

watch(searchQuery, (q) => {
  selectedUser.value = null;

  if (q.length < 2) {
    runSearch.cancel();
    searchResults.value = [];
    return;
  }

  runSearch(q);
});
</script>

<style lang="less" scoped>
.search-results {
  height: 240px;
  overflow-y: auto;
  border: 1px solid @stroke-default;
  border-radius: 8px;
}
</style>
